import { useState } from "react";
import { modals } from "@mantine/modals";
import {
  ActionIcon,
  Button,
  Group,
  Image,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconRefresh, IconTrash } from "@tabler/icons-react";
import { pickAndUploadIcons } from "./upload-icons";
import { VariantSelect, type LayoutVariant } from "./variant-select";

export interface ChipConfig {
  iconSrc: string;
  iconAlt: string;
  label: string;
  href: string;
  variant: LayoutVariant;
}

function ConfigureChipsForm({
  initial,
  modalId,
  resolve,
  pageId,
  submitLabel,
}: {
  initial: ChipConfig[];
  modalId: string;
  resolve: (val: ChipConfig[] | null) => void;
  pageId?: string;
  submitLabel: string;
}) {
  const [chips, setChips] = useState(initial);

  const update = (i: number, patch: Partial<ChipConfig>) =>
    setChips((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const removeRow = (i: number) =>
    setChips((cs) => cs.filter((_, idx) => idx !== i));

  const replaceIcon = async (i: number) => {
    if (!pageId) return;
    const [ic] = await pickAndUploadIcons(pageId);
    if (!ic) return;
    update(i, { iconSrc: ic.src, iconAlt: ic.alt });
  };

  // Order matters: settle() must fire BEFORE modals.close(), because
  // modals.close() synchronously triggers onClose → settle(null), which
  // then blocks our intended payload because `settled` is already true.
  const submit = () => {
    resolve(chips);
    modals.close(modalId);
  };
  const cancel = () => {
    resolve(null);
    modals.close(modalId);
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Edit the label, link, and icon for each chip. Replace uploads a new
        image to storage.
      </Text>
      {chips.map((c, i) => (
        <Group key={i} wrap="nowrap" align="flex-start" gap="sm">
          <Stack gap={4} align="center" style={{ flexShrink: 0 }}>
            <Image
              src={c.iconSrc}
              alt={c.iconAlt}
              w={48}
              h={48}
              fit="contain"
              radius="sm"
              style={{
                border: "1px solid var(--mantine-color-default-border)",
              }}
            />
            {pageId ? (
              <Tooltip label="Replace icon" withArrow>
                <ActionIcon
                  size="xs"
                  variant="default"
                  onClick={() => replaceIcon(i)}
                  aria-label="Replace icon"
                >
                  <IconRefresh size={12} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Stack>
          <Stack gap={4} style={{ flex: 1 }}>
            <TextInput
              size="xs"
              label="Label"
              value={c.label}
              onChange={(e) => update(i, { label: e.currentTarget.value })}
            />
            <TextInput
              size="xs"
              label="Link (optional)"
              placeholder="https://… or /path"
              value={c.href}
              onChange={(e) => update(i, { href: e.currentTarget.value })}
            />
            <VariantSelect
              value={c.variant}
              onChange={(variant) => update(i, { variant })}
            />
          </Stack>
          {chips.length > 1 ? (
            <Tooltip label="Remove chip" withArrow>
              <ActionIcon
                size="xs"
                variant="subtle"
                color="red"
                onClick={() => removeRow(i)}
                aria-label="Remove chip"
              >
                <IconTrash size={12} />
              </ActionIcon>
            </Tooltip>
          ) : null}
        </Group>
      ))}
      <Group justify="flex-end">
        <Button variant="default" size="xs" onClick={cancel}>
          Cancel
        </Button>
        <Button size="xs" onClick={submit} disabled={chips.length === 0}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Open a one-shot modal that lets the user edit label + href per uploaded
 * chip icon before the block is inserted. Resolves to the configured array,
 * or null when the user cancels. Used by the "Icon chip grid" slash item.
 */
export function openConfigureChips(
  initial: ChipConfig[],
  options: { pageId?: string; mode?: "insert" | "edit" } = {},
): Promise<ChipConfig[] | null> {
  return new Promise((resolve) => {
    const modalId = `configure-chips-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const settle = (val: ChipConfig[] | null) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    const submitLabel = options.mode === "edit" ? "Save" : "Insert";
    modals.open({
      modalId,
      title: options.mode === "edit" ? "Edit chips" : "Configure chips",
      size: "md",
      withCloseButton: true,
      closeOnEscape: true,
      closeOnClickOutside: false,
      // Fires for Escape / close button / outside click too — guard with the
      // `settled` flag so a real submit/cancel from inside the form wins.
      onClose: () => settle(null),
      children: (
        <ConfigureChipsForm
          initial={initial}
          modalId={modalId}
          resolve={settle}
          pageId={options.pageId}
          submitLabel={submitLabel}
        />
      ),
    });
  });
}
